import AppModule from "@/store/modules/app";
import DeviceModule from "@/store/modules/device";
import UserModule from "@/store/modules/user";

import dayjs from "dayjs";
import { generateLongId } from "../utils/helpers/app";
import { request3 } from "@/utils/request";
import { ILog } from "@/interfaces/logs/poslogs";

import BookingModule from "@/store/modules/booking";
import { dexieDB } from "./dexie-database";
import { gzipCompressJson } from "@/utils/helpers/common";

export enum LogLevel {
  Info = "INFO",
  Error = "ERROR",
}

class Logger {
  meta() {
    return {
      // These are default for each app
      env: import.meta.env.VITE_ENV,
      timestamp: Date.now(),
      app: import.meta.env.VITE_NAME,
      logid: generateLongId(),

      // These are specifics for New POS
      date: dayjs().format("YYYY-MM-DD"),
      device: DeviceModule.newLogicDeviceId || "",
      user: UserModule.userId,
      group: UserModule.getUserGroupId,
      lang: AppModule.getLanguageTag,
      // Version of app should be added
      version: AppModule.appVersion,
      desktopAppVersion: DeviceModule.hardwareInfo?.desktopAppVersion || "",

      booking: BookingModule.selectedBookingUUID,
      bookingReference: BookingModule.selectedBooking
        ? BookingModule.selectedBooking.reference
        : "",
      hardwareUuid:
        DeviceModule.hardwareInfo && DeviceModule.hardwareInfo.id
          ? DeviceModule.hardwareInfo.id
          : "",
      hardwareHash: DeviceModule.getHardwareHash || "",
      orderSessionId: BookingModule.currentOrderSessionId,
    };
  }

  async info(msg: string, data: any = {}, logLevel: LogLevel = LogLevel.Info) {
    const meta = this.meta();
    const compressed = msg.startsWith("(compressed)");
    let params;
    try {
      // try to compress the large json file and log it as a base64 encoded buffer
      params = compressed
        ? Buffer.from(await gzipCompressJson(data)).toString("base64")
        : JSON.stringify({ ...data }, null, 2);
    } catch (error) {
      params = JSON.stringify({ ...data }, null, 2);
    }

    try {
      this.store({
        _id: meta.logid,
        log: {
          logLevel,
          message: `${dayjs(meta.timestamp).toISOString()} INFO : ${msg}`,
          params,
          ...meta,
        },
      });
    } catch (error) {
      // Putting it in setTimeout because we don't block
      // app execution because of logging
      setTimeout(() => {
        throw new Error(`Error while info logging ${error}`);
      });
    }
  }

  /**
   * Same as info(), but POSTs the logs to remote server directly
   * @param msg
   * @param data
   * @param logLevel
   */
  async infoDirect(
    msg: string,
    data: any = {},
    logLevel: LogLevel = LogLevel.Info
  ) {
    const meta = this.meta();
    const compressed = msg.startsWith("(compressed)");
    let params;
    try {
      // try to compress the large json file and log it as a base64 encoded buffer
      params = compressed
        ? Buffer.from(await gzipCompressJson(data)).toString("base64")
        : JSON.stringify({ ...data }, null, 2);
    } catch (error) {
      params = JSON.stringify({ ...data }, null, 2);
    }

    try {
      await postLogs([
        {
          logLevel,
          message: `${dayjs(meta.timestamp).toISOString()} INFO : ${msg}`,
          params,
          ...meta,
        },
      ]);
    } catch (error) {
      throw new Error(`Error while info logging ${error}`);
    }
  }

  /**
   * Sotres a log and persists it to localstorage.
   * Note that logs are not saved while this class is syncing
   * It will attempt to save it for 10 seconds before throwing an error
   */
  async store(log: ILog) {
    await dexieDB.logs.add(log.log);
  }
}

export const LogService = new Logger();

/**
 * POST logs to remote server
 * @param data
 * @returns
 */
export async function postLogs(logs: unknown): Promise<any> {
  const data = { logs: logs };
  const response = await request3({
    method: "POST",
    data,
  });

  return response;
}
